import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/Nav";
import toast from "react-hot-toast";
import { X, DollarSign, Hash, Clock4, ChevronRight } from "lucide-react";
import getClientConfig from '../config';

const { API_BASE_URL } = getClientConfig();


export default function RateCardPage() {
    useEffect(() => {
        document.title = "Rate Cards | Voice Intelligence Portal";
      }, []);
  const [rateCards, setRateCards] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    pattern: "",
    description: "",
    rate: 0,
    min_duration: 60,
    increment: 60,
  });

  const token = localStorage.getItem("auth_token");

  useEffect(() => {
    loadRateCards();
  }, []);

  const loadRateCards = () => {
    fetch(`${API_BASE_URL}/voice/rates`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setRateCards(data.rates || []))
      .catch(() => toast.error("Failed to load rate cards"));
  };

  const handleOpenModal = (rate = null) => {
    if (rate) {
      setEditing(rate.id);
      setForm({
        pattern: rate.pattern,
        description: rate.description,
        rate: parseFloat(rate.rate),
        min_duration: rate.min_duration,
        increment: rate.increment,
      });
    } else {
      setEditing(null);
      setForm({
        pattern: "",
        description: "",
        rate: 0,
        min_duration: 60,
        increment: 60,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.pattern || isNaN(form.rate)) {
      toast.error("Pattern and valid Rate are required");
      return;
    }

    const url = editing
      ? `${API_BASE_URL}/voice/rates/update/${editing}`
      : `${API_BASE_URL}/voice/rates/update`;
    const method = editing ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(form),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        toast.success(editing ? "Rate updated" : "Rate created");
        loadRateCards();
        handleCloseModal();
      })
      .catch(() => toast.error("Failed to save rate"));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this rate card?")) return;

    fetch(`${API_BASE_URL}/voice/rates/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        toast.success("Rate deleted");
        loadRateCards();
      })
      .catch(() => toast.error("Failed to delete rate"));
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">Rate Cards</h2>
          <button
            onClick={() => handleOpenModal()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            + Add Rate
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full bg-white border rounded shadow text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-3 text-left">Pattern</th>
                <th className="p-3 text-left">Description</th>
                <th className="p-3 text-left">Rate</th>
                <th className="p-3 text-left">Min</th>
                <th className="p-3 text-left">Inc</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rateCards.map((rate) => (
                <tr key={rate.id} className="border-t hover:bg-gray-50">
                  <td className="p-2 font-mono">{rate.pattern}</td>
                  <td className="p-2">{rate.description}</td>
                  <td className="p-2">{rate.rate}</td>
                  <td className="p-2">{rate.min_duration}</td>
                  <td className="p-2">{rate.increment}</td>
                  <td className="p-2 flex gap-2">
                    <button
                      onClick={() => handleOpenModal(rate)}
                      className="text-blue-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(rate.id)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {rateCards.length === 0 && (
                <tr>
                  <td colSpan="6" className="p-4 text-center text-gray-500">
                    No rate cards found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-8 relative border">
      {/* Close icon */}
      <button
        className="absolute top-4 right-4 text-gray-500 hover:text-red-500 transition"
        onClick={handleCloseModal}
      >
        <X className="w-6 h-6" />
      </button>

      {/* Title */}
      <div className="mb-6 flex items-center gap-2">
        <ChevronRight className="text-blue-600" />
        <h2 className="text-xl font-bold text-gray-800">
          {editing ? "Edit Rate Card" : "Create New Rate Card"}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-6">
        {/* Pattern */}
        <div className="col-span-2">
          <label className="text-sm text-gray-600 font-medium flex items-center gap-2 mb-1">
            <Hash className="w-4 h-4 text-blue-500" />
            Pattern (Regex) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={form.pattern}
            onChange={(e) => setForm({ ...form, pattern: e.target.value })}
            required
          />
        </div>

        {/* Rate */}
        <div>
          <label className="text-sm text-gray-600 font-medium flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-green-500" />
            Rate <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.0001"
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={form.rate}
            onChange={(e) => setForm({ ...form, rate: parseFloat(e.target.value) })}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="text-sm text-gray-600 font-medium mb-1 block">Description</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        {/* Min Duration */}
        <div>
          <label className="text-sm text-gray-600 font-medium flex items-center gap-2 mb-1">
            <Clock4 className="w-4 h-4 text-indigo-500" />
            Min Duration (sec)
          </label>
          <input
            type="number"
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={form.min_duration}
            onChange={(e) => setForm({ ...form, min_duration: parseInt(e.target.value) })}
          />
        </div>

        {/* Increment */}
        <div>
          <label className="text-sm text-gray-600 font-medium flex items-center gap-2 mb-1">
            <Clock4 className="w-4 h-4 text-indigo-500" />
            Increment (sec)
          </label>
          <input
            type="number"
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={form.increment}
            onChange={(e) => setForm({ ...form, increment: parseInt(e.target.value) })}
          />
        </div>

        {/* Action buttons */}
        <div className="col-span-2 flex justify-end gap-3 mt-4">
          <button
            type="button"
            onClick={handleCloseModal}
            className="bg-gray-200 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            {editing ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  </div>
)}


    </DashboardLayout>
  );
}
